import { uploadftpfile } from "../services/api/FtpApi";


/**
 * Upload a file via FTP and return the processed response
 * @param {File} file - The file to upload
 * @param {string} filePath - The FTP path where file should be uploaded (optional)
 * @param {function} onProgress - Callback function for upload progress (optional)
 * @returns {Promise<Object>} - Returns processed upload response
 */
export const uploadFileViaFTP = async (file, filePath = '', onProgress = null) => {
  if (!file) {
    throw new Error('No file provided for upload');
  }

  try {
    // Create FormData object
    const formData = new FormData();
    formData.append('file', file);
    
    // Add filePath if provided
    if (filePath) {
      formData.append('filePath', filePath);
    }

    // Call the FTP upload API
    const response = await uploadftpfile(formData);
    
    if (!response.data || !response.data.success) {
      throw new Error(response.data?.message || 'Upload failed');
    }

    const { viewUrl, downloadUrl, directUrl, fileInfo } = response.data;
    
    // Extract the image URL part after 'view/' from viewUrl
    let imageUrl = '';
    if (viewUrl) {
      const viewIndex = viewUrl.indexOf('/ftp/view/');
      if (viewIndex !== -1) {
        imageUrl = viewUrl.substring(viewIndex + '/ftp/view/'.length);
      }
    }

    return {
      success: true,
      imageUrl, // This is what you'll save in your task
      viewUrl,
      downloadUrl,
      directUrl,
      fileInfo: {
        originalName: fileInfo.originalName,
        size: fileInfo.size,
        mimetype: fileInfo.mimetype,
        uploadPath: fileInfo.uploadPath
      }
    };

  } catch (error) {
    console.error('FTP Upload Error:', error);
    throw new Error(error.response?.data?.message || error.message || 'File upload failed');
  }
};

/**
 * Upload a file with progress tracking
 * @param {File} file - The file to upload
 * @param {string} filePath - The FTP path where file should be uploaded (optional)
 * @param {function} onProgress - Callback function for upload progress
 * @param {function} onSuccess - Callback function for successful upload
 * @param {function} onError - Callback function for upload error
 */
export const uploadFileWithProgress = async (
  file, 
  filePath = '', 
  onProgress = null, 
  onSuccess = null, 
  onError = null
) => {
  try {
    // Simulate progress since FTP might not provide real-time progress
    if (onProgress) {
      onProgress(10);
    }

    const result = await uploadFileViaFTP(file, filePath);
    
    if (onProgress) {
      onProgress(100);
    }

    if (onSuccess) {
      onSuccess(result);
    }

    return result;

  } catch (error) {
    if (onError) {
      onError(error);
    }
    throw error;
  }
};

export default {
  uploadFileViaFTP,
  uploadFileWithProgress
};